import { apiEsign } from "@/api/esign.api.ts";
import { documentsKeys } from "@/queries/use-documents.query.ts";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { preboardingKeys } from "@/queries/preboarding/use-preboarding.query.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiEsignDocumentFilter,
  ApiEsignEnvelopeFilter,
  ApiEsignEnvelopePreviewFilter,
  ApiEsignEnvelopeSave,
  ApiEsignMarkupSave,
  ApiEsignOtpVerify,
  ApiEsignRequestDecline,
  ApiEsignSignRequest,
} from "@/types/esign.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const esignKeys = createQueryKeys("esign", {
  documentCounts: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getDocumentCounts(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  documentsSearch: (filter: ApiEsignDocumentFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.searchDocuments(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  envelopesSearch: (filter: ApiEsignEnvelopeFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.searchEnvelopes(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  envelopesSearchInfinite: (filter: ApiEsignEnvelopeFilter) => ({
    queryKey: [filter],
  }),
  markup: (documentId: number) => ({
    queryKey: [{ documentId }],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getMarkup(documentId, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? null;
    },
  }),
  envelope: (envelopeId: string) => ({
    queryKey: [{ envelopeId }],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getEnvelope(envelopeId, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  request: (requestId: string) => ({
    queryKey: [{ requestId }],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getRequest(requestId, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  signingContext: (requestId: string) => ({
    queryKey: [{ requestId }],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getSigningContext(requestId, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  evidenceReport: (envelopeId: string) => ({
    queryKey: [{ envelopeId }],
    queryFn: async ({ signal }) => {
      const result = await apiEsign.getEvidenceReport(envelopeId, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useEsignDocumentCounts = () => {
  return useQuery(esignKeys.documentCounts);
};

export const useEsignDocumentsSearch = (filter: ApiEsignDocumentFilter, enabled = true) => {
  return useQuery({ ...esignKeys.documentsSearch(filter), enabled });
};

export const useEsignEnvelopesSearch = (filter: ApiEsignEnvelopeFilter) => {
  return useQuery(esignKeys.envelopesSearch(filter));
};

export const useEsignEnvelopesInfinite = (filter: ApiEsignEnvelopeFilter, enabled = true) => {
  return useInfiniteQuery({
    queryKey: esignKeys.envelopesSearchInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiEsign.searchEnvelopes({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage && lastPage.metadata.page_number < lastPage.metadata.total_pages
        ? lastPage.metadata.page_number + 1
        : undefined,
    enabled,
  });
};

export const useEsignMarkup = (documentId: number, enabled = true) => {
  return useQuery({ ...esignKeys.markup(documentId), enabled });
};

export const useEsignEnvelope = (envelopeId: string, enabled = true) => {
  return useQuery({ ...esignKeys.envelope(envelopeId), enabled });
};

export const useEsignRequest = (requestId: string, enabled = true) => {
  return useQuery({ ...esignKeys.request(requestId), enabled });
};

export const useEsignSigningContext = (requestId: string, enabled = true) => {
  return useQuery({ ...esignKeys.signingContext(requestId), enabled });
};

export const useEsignEvidenceReport = (envelopeId: string, enabled = true) => {
  return useQuery({ ...esignKeys.evidenceReport(envelopeId), enabled });
};

export const useEsignEvidenceReportPdf = () => {
  return useMutation({
    mutationFn: async (envelopeId: string) => {
      const result = await apiEsign.getEvidenceReportPdf(envelopeId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { data: result.data, headers: result.headers };
    },
  });
};

interface EsignMarkupSaveParams {
  documentId: number;
  exists: boolean;
  payload: ApiEsignMarkupSave;
}

export const useEsignMarkupSave = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ documentId, exists, payload }: EsignMarkupSaveParams) => {
      const result = exists
        ? await apiEsign.updateMarkup(documentId, payload)
        : await apiEsign.createMarkup(documentId, payload);

      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: esignKeys.markup(variables.documentId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEsignEnvelopeCreate = () => {
  return useMutation({
    mutationFn: async (payload: ApiEsignEnvelopeSave) => {
      const result = await apiEsign.createEnvelope(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useEsignRequestDecline = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      requestId,
      payload,
    }: {
      requestId: string;
      payload: ApiEsignRequestDecline;
    }) => {
      const result = await apiEsign.declineRequest(requestId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday().queryKey });
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
  });
};

export const useEsignRequestSign = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      requestId,
      payload,
    }: {
      requestId: string;
      payload?: ApiEsignSignRequest;
    }) => {
      const result = await apiEsign.signRequest(requestId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: employeeTodayKeys.getToday().queryKey });
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
  });
};

export const useEsignEnvelopeCancel = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (envelopeId: string) => {
      const result = await apiEsign.cancelEnvelope(envelopeId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: esignKeys._def });
      void queryClient.invalidateQueries({ queryKey: documentsKeys._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEsignEnvelopePreview = () => {
  return useMutation({
    mutationFn: async (payload: ApiEsignEnvelopePreviewFilter) => {
      const result = await apiEsign.previewEnvelope(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useEsignOtpRequest = () => {
  return useMutation({
    mutationFn: async () => {
      const result = await apiEsign.requestOtp();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useEsignOtpVerify = () => {
  return useMutation({
    mutationFn: async (payload: ApiEsignOtpVerify) => {
      const result = await apiEsign.verifyOtp(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};
